import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  Tabs,
  Tab,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  CardMedia,
} from '@mui/material';
import {
  Groups,
  Speed,
  CloudUpload,
  Edit,
  Share,
  CheckCircle,
  Storage,
  TrendingUp,
  FormatQuote,
  Image,
  VideoLibrary,
  Article,
  Email,
  Campaign,
  ArrowForward,
  LocalOffer,
  Search,
  Bookmark,
  Description,
  Business,
  Security,
  History,
  AutoAwesome,
  Audiotrack,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import searchAbilitiesImage from './search-features.jpg';
import viewEditingImage from './view-editor.jpg';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 4 }}>{children}</Box>}
    </div>
  );
};

const FeaturesPage: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const [tabValue, setTabValue] = React.useState(0);

  // Map hash values to tab indexes
  const hashToTabIndex: { [key: string]: number } = {
    '#content-organization': 0,
    '#content-publishing': 1,
    '#team-collaboration': 2,
    '#administration': 3,
  };

  // Handle hash navigation on mount and when hash changes
  React.useEffect(() => {
    const hash = location.hash;
    const tabIndex = hashToTabIndex[hash];
    if (tabIndex !== undefined) {
      setTabValue(tabIndex);
    }
  }, [location.hash]);

  const featureCategories = [
    {
      title: 'Content Organization',
      icon: <LocalOffer />,
      features: [
        {
          icon: <LocalOffer />,
          title: 'Multi-Dimensional Tagging',
          description: 'Far more powerful than folders. Apply unlimited tags to each item and organize content in multiple categories simultaneously.',
          capabilities: [
            'Unlimited tags per content item',
            'Tag groups for related categories (Product Line, Target Market, etc.)',
            'Ordered tag groups for process stages (sales funnel steps)',
            'Unordered tag groups for classifications (industries, applications)',
            'Single-selection or multiple-selection tag groups',
            'Content appears in all applicable category views automatically',
          ],
        },
        {
          icon: <Search />,
          title: 'AI-Powered Search & Discovery',
          description: 'Find exactly what you need with hybrid search that combines keyword precision with AI-driven semantic understanding. Type plain English — no search syntax required.',
          capabilities: [
            'Natural language queries ("find training videos for conference rooms")',
            'Semantic search surfaces relevant content even without exact keyword matches',
            'Keyword search across titles, descriptions, AI summaries, and transcripts',
            'Search inside video and audio by transcript content with timestamp results',
            'Search inside PDFs and documents by page and section',
            'Filter by file type, tags, ratings, date, and bookmarks',
            'Results explained: see why each item matched',
            'Switch result tiles between document thumbnails and file-type illustrations',
          ],
        },
        {
          icon: <Bookmark />,
          title: 'Personal Bookmarks & Ratings',
          description: 'Users can bookmark favorite content and rate items to help the team discover the best materials.',
          capabilities: [
            'Quick bookmark toggle on any content item',
            'Personal bookmark library for fast access',
            '5-star rating system for quality feedback',
            'Filter search results by ratings',
            'Provide written feedback on any item',
          ],
        },
      ],
    },
    {
      title: 'Content Publishing',
      icon: <Description />,
      features: [
        {
          icon: <Description />,
          title: 'Upload Any File Type',
          description: 'Support for all content types and large files. Single file or bulk upload capabilities.',
          capabilities: [
            'All file types supported (documents, images, videos, archives)',
            'Large file support (up to 10GB per file on paid plans)',
            'Single file upload with drag-and-drop',
            'Bulk upload for multiple files at once',
            'Automatic file type detection and icon',
            'Progress indicators for large uploads',
          ],
        },
        {
          icon: <AutoAwesome />,
          title: 'AI Content Enrichment',
          description: 'Every file is automatically analyzed after upload. AI generates descriptions, summaries, title suggestions, and applies your tag taxonomy — with zero manual effort.',
          capabilities: [
            'AI-generated 1–3 sentence description for every uploaded file',
            'Structured summary: key topics, intended audience, entities mentioned',
            'Clean title suggestions for poorly named files (opt-in auto-apply)',
            'Automatic tag classification using your existing tag taxonomy',
            'Audio and video transcription with timestamps',
            'Visual descriptions for images, diagrams, slides, and scanned PDFs',
            'Enrichment runs in the background — files are accessible immediately',
            'Processing status indicator disappears once fully searchable',
          ],
        },
        {
          icon: <Edit />,
          title: 'Custom Content Views',
          description: 'Create formatted, structured pages of curated content with the WYSIWYG View Editor — then share the whole layout publicly with a single link.',
          capabilities: [
            'WYSIWYG editor with live preview',
            'Component-based design (text, images, selected content, queries)',
            'Embed specific content items or dynamic queries',
            'Rich text formatting and layout control',
            'Per-panel thumbnail or illustration display — match each section to its visual intent',
            'Configurable page size and item caps on Content Query blocks, with auto-paginating controls when results exceed the page size',
            'Publish a View as public and share the full page layout via a single link — recipients see the same rich rendering your team does, with no account required',
            'Perfect for sales kits, product comparisons, resource centers',
            'Embed external web pages or referenced content',
          ],
        },
        {
          icon: <Share />,
          title: 'Secure Public Sharing',
          description: 'Share single files or entire curated Views with feature-rich, secure public links that render the way your team sees them.',
          capabilities: [
            'Share a single file or an entire View page-builder layout',
            'Full-fidelity public View rendering — sections, rows, rich text, dividers, Content Pick and Content Query tiles, all with the same look as in-app',
            'Click-through tiles inside a shared View — recipients can drill into any tile to view the underlying content, then return without losing their place',
            'Nested-View drill-in — tiles that point at another View open that View with its own embedded content blocks active',
            'Strict per-team content scoping — a shared View can never surface content from another team, even if a block is misconfigured to reference it',
            'Publishers control which content can be shared',
            'Optional expiration dates for time-limited access',
            'Optional password protection — visitors enter the password once per browser tab and can browse the View without being re-challenged',
            'Password change immediately invalidates active visitor sessions',
            'Usage limits (max number of times link can be used)',
            'Revoke links at any time',
            'Monitor link usage and access statistics',
            'Optional descriptions for link management',
          ],
        },
        {
          icon: <History />,
          title: 'Version Management',
          description: 'Keep a complete history of every file update. Control what readers see and when, without ever losing previous work.',
          capabilities: [
            'Upload new versions without disrupting readers',
            'Draft new versions and activate them on your schedule',
            'Replace current version instantly to fix mistakes',
            'Full version history with upload dates and uploader names',
            'Activate any previous version with one click',
            'Per-version view and download analytics',
          ],
        },
      ],
    },
    {
      title: 'Team Collaboration',
      icon: <Groups />,
      features: [
        {
          icon: <Groups />,
          title: 'Multi-Company Team Support',
          description: 'Built from the ground up for teams that span multiple companies and organizations.',
          capabilities: [
            'Join as an individual user with one login',
            'Join any number of teams managed by anyone',
            'Teams can include members from different companies',
            'Team managers easily invite or remove members',
            'No complex cross-company permissions needed',
            'Seamless collaboration across organizational boundaries',
          ],
        },
        {
          icon: <Business />,
          title: 'Organization Management',
          description: 'Organizations group multiple teams and subscriptions for centralized administration.',
          capabilities: [
            'Create Organizations to manage multiple teams',
            'Any number of teams under one Organization',
            'Shared subscription pools across teams',
            'Centralized billing and team management',
            'Separate billing control from team management',
            'No account hopping—manage everything in one place',
          ],
        },
        {
          icon: <CloudUpload />,
          title: 'Revolutionary Concurrent Pricing',
          description: 'Fair pricing based on simultaneous usage, not per-seat counts. Perfect for mixed-intensity teams.',
          capabilities: [
            'Add unlimited team members for free',
            'Pay only for concurrent active users',
            '10 concurrent seats typically serve 100+ users',
            'Heavy and light users share seats naturally',
            '80-90% cost reduction vs per-seat licensing',
            'Share subscriptions across multiple teams for even better efficiency',
          ],
        },
      ],
    },
    {
      title: 'Administration',
      icon: <Security />,
      features: [
        {
          icon: <Security />,
          title: 'Easy Administration',
          description: 'Simple, intuitive administration that does not require IT staff or extensive training.',
          capabilities: [
            'Intuitive admin interface for all operations',
            'Team managers control their own teams',
            'Organization admins manage billing and subscriptions',
            'Bulk operations for common tasks',
            'Self-service user management',
            'No IT staff required for daily operations',
          ],
        },
        {
          icon: <TrendingUp />,
          title: 'Flexible Subscription Management',
          description: 'Buy fewer, larger subscriptions and share them across multiple teams for maximum efficiency.',
          capabilities: [
            'One subscription can support multiple teams',
            'Shared concurrent user pool across teams',
            'Shared storage allocation across teams',
            'Volume discounts on larger plans',
            'Dramatically improves utilization efficiency',
            'Simplified billing for multiple teams',
          ],
        },
        {
          icon: <Storage />,
          title: 'Scalable Add-Ons',
          description: 'Scale your capacity with simple add-on packs. No forced tier upgrades or feature bundles.',
          capabilities: [
            'Add concurrent user packs as needed',
            'Add storage packs independently',
            'Combine add-ons with any base plan',
            'Volume discounts on add-on packs',
            'Adjust capacity up or down anytime',
            'Pay only for what you actually need',
          ],
        },
      ],
    },
  ];

  const contentTypes = [
    { icon: <Article />, label: 'Documents', description: 'PDFs, Word docs, spreadsheets, and more' },
    { icon: <Image />, label: 'Images', description: 'Photos, diagrams, infographics, and graphics' },
    { icon: <VideoLibrary />, label: 'Videos', description: 'Training videos, demos, and presentations' },
    { icon: <Campaign />, label: 'Marketing Materials', description: 'Brochures, datasheets, and collateral' },
    { icon: <FormatQuote />, label: 'Presentations', description: 'PowerPoint and Keynote files' },
    { icon: <Description />, label: 'Technical Docs', description: 'Specifications, manuals, and guides' },
    { icon: <Email />, label: 'Templates', description: 'Proposal templates and reusable content' },
    { icon: <Audiotrack />, label: 'Audio', description: 'MP3, WAV, and M4A files with auto-transcription' },
    { icon: <Storage />, label: 'Archives', description: 'ZIP files and compressed collections' },
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
          pt: { xs: 6, md: 10 },
          pb: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                mb: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Simple, Powerful Content Sharing
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
              Everything you need to organize, find, share, and manage content across teams and organizations—with AI-powered enrichment and search—at a fraction of traditional costs
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                component={Link}
                to="/signup"
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
              >
                Start Now Free
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Feature Categories with Tabs */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {featureCategories.map((category, index) => (
              <Tab
                key={index}
                label={category.title}
                icon={category.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Box>

        {featureCategories.map((category, categoryIndex) => (
          <TabPanel key={categoryIndex} value={tabValue} index={categoryIndex}>
            <Grid container spacing={4}>
              {category.features.map((feature, featureIndex) => (
                <Grid item xs={12} md={4} key={featureIndex}>
                  <Card
                    sx={{
                      height: '100%',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: 4,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {feature.icon}
                        <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
                          {feature.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {feature.description}
                      </Typography>
                      <List dense>
                        {feature.capabilities.map((capability, index) => (
                          <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <CheckCircle color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={capability}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </TabPanel>
        ))}
      </Container>

      {/* Content Types Section */}
      <Box sx={{ backgroundColor: 'background.default', py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ mb: 2, fontWeight: 700 }}>
              Support for Every Type of Content
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Upload and share any file type your team needs—documents, images, videos, and more
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {contentTypes.map((type) => (
              <Grid item xs={12} sm={6} md={3} key={type.label}>
                <Paper
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    height: '100%',
                    transition: 'transform 0.2s',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 3,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      backgroundColor: `${theme.palette.primary.main}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      color: 'primary.main',
                    }}
                  >
                    {type.icon}
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {type.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {type.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Visual Feature Demo Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" sx={{ mb: 2, fontWeight: 700 }}>
            See It In Action
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Powerful features that make content sharing effortless
          </Typography>
        </Box>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                p: 4,
                borderRadius: 3,
                backgroundColor: 'background.paper',
                boxShadow: 1,
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 0,
                  borderRadius: 3,
                  overflow: 'hidden',
                  mb: 3,
                  width: '100%',
                }}
              >
                <CardMedia
                  component="img"
                  image={searchAbilitiesImage}
                  alt="Advanced Search & Filtering - Search with multiple filters, tags, and ratings"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                />
              </Paper>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  Advanced Search & Filtering
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Find exactly what you need with powerful search across tags, ratings, and file types
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                p: 4,
                borderRadius: 3,
                backgroundColor: 'background.paper',
                boxShadow: 1,
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 0,
                  borderRadius: 3,
                  overflow: 'hidden',
                  mb: 3,
                  width: '100%',
                }}
              >
                <CardMedia
                  component="img"
                  image={viewEditingImage}
                  alt="WYSIWYG View Editor - Create and edit curated content pages"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                />
              </Paper>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  WYSIWYG View Editor
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Create professional sales kits and resource pages with our intuitive visual editor
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Performance Section */}
      <Box sx={{ backgroundColor: 'background.default', py: 10 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ mb: 3, fontWeight: 700 }}>
                Simple Yet Powerful
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                All the capabilities you need without unnecessary complexity
              </Typography>
              <List>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <AutoAwesome color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="AI enrichment on every upload"
                    secondary="Automatic descriptions, summaries, transcripts, and tag classification"
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <Speed color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Hybrid search that understands context"
                    secondary="Keyword and semantic search combined — find content with natural language"
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <CloudUpload color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Reliable cloud hosting"
                    secondary="Cloud-hosted with excellent availability when your team needs content"
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <Storage color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Flexible storage tiers"
                    secondary="Start with 2GB free, scale to terabytes with simple add-on packs"
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <TrendingUp color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="No training required"
                    secondary="Intuitive interface gets teams productive immediately"
                  />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 4,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.secondary.main}10 100%)`,
                }}
              >
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h3" color="primary">
                        &lt;500ms
                      </Typography>
                      <Typography variant="body2">Search Time</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h3" color="primary">
                        99.9%
                      </Typography>
                      <Typography variant="body2">Uptime</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h3" color="primary">
                        10M+
                      </Typography>
                      <Typography variant="body2">Downloads/Day</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h3" color="primary">
                        50+
                      </Typography>
                      <Typography variant="body2">File Types</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          py: 8,
          color: 'white',
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" sx={{ mb: 3, fontWeight: 700 }}>
            Ready to Simplify Content Sharing?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.95 }}>
            See how Tier One Content can reduce your costs by 80-90%
          </Typography>
          <Button
            component={Link}
            to="/signup"
            variant="contained"
            size="large"
            sx={{
              py: 1.5,
              px: 4,
              background: '#ffffff',
              color: theme.palette.primary.dark,
              fontWeight: 700,
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.92)',
              },
            }}
          >
            Start Free Forever
          </Button>
        </Container>
      </Box>
    </>
  );
};

export default FeaturesPage;